import axios, { AxiosError, AxiosResponse } from 'axios';

import { apiConfig } from '@/api';
import { TaskResultResponse } from '@/api/generated/MNT';
import { sleep } from '@/async-result/utils';
import * as Sentry from '@/sentry';

(window as any).axios = axios;

/** Fetches the task's current state, failing on an HTTP error rather than polling it forever. */
const fetchTaskResult = async (link: string): Promise<TaskResultResponse> => {
  const res = await fetch(link);
  if (!res.ok) {
    throw new Error(`Background task poll failed with status ${res.status}`);
  }
  return res.json();
};

export const interceptBackgroundTask = async (response: AxiosResponse<TaskResultResponse>): Promise<AxiosResponse> => {
  if (response.status !== 202 || !response.data.link) {
    return response;
  }

  const { config } = response;

  let step = 1;

  while (true) {
    await sleep(Math.min(step, 10) * 1000);
    step += 1;
    const res = await fetchTaskResult(response.data.link);
    if (res.task_is_pending || !res.result_http_status) {
      continue;
    }
    if (res.task_is_error) {
      console.error(`Background task '${config.url}' failed:`, res);
      Sentry.captureException(new Error(`Unexpected background task failure: ${config.url}`));
    }
    const resp = {
      status: res.result_http_status,
      statusText: '',
      data: res.result_http_body_json,
      headers: response.headers,
      config: response.config,
      request: response.request,
    };
    if (!config.validateStatus?.(resp.status)) {
      throw new AxiosError(
        'Request failed with status code ' + resp.status,
        [AxiosError.ERR_BAD_REQUEST, AxiosError.ERR_BAD_RESPONSE][Math.floor(resp.status / 100) - 4],
        resp.config,
        resp.request,
        resp,
      );
    }
    // Resolves with the raw task result rather than `resp`, as it always has.
    return res as unknown as AxiosResponse;
  }
};
